import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Select, Label } from '../../../components/ui';
import { CreateConversationData } from '../useData';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface MessageInputProps {
  conversationType: 'TICKET' | 'TASK';
  ticketId?: number;
  taskId?: number;
  replyToId?: number;
  senderType: 'STAFF' | 'CLIENT';
  staffUserId?: number;
  clientId?: number;
  onSubmit: (data: CreateConversationData) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  conversationType,
  ticketId,
  taskId,
  replyToId,
  senderType,
  staffUserId,
  clientId,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { showError } = useNotification();
  const { colors } = useTheme();
  const [message, setMessage] = useState('');
  const [selectedSenderType, setSelectedSenderType] = useState<'STAFF' | 'CLIENT'>(senderType);
  const [selectedStaffUserId, setSelectedStaffUserId] = useState<number | undefined>(staffUserId);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(clientId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      showError('Message cannot be empty');
      return;
    }

    try {
      await onSubmit({
        conversationType,
        ticketId,
        taskId,
        message,
        senderType: selectedSenderType,
        staffUserId: selectedSenderType === 'STAFF' ? selectedStaffUserId : undefined,
        clientId: selectedSenderType === 'CLIENT' ? selectedClientId : undefined,
        replyToId,
      });
      setMessage('');
    } catch (error: any) {
      showError(error?.message || 'Failed to send message');
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn('border rounded-lg p-4', colors.surface, colors.border)}>
      <div className="space-y-4">
        {conversationType === 'TICKET' && (
          <div>
            <Label htmlFor="senderType">Sender Type</Label>
            <Select
              id="senderType"
              value={selectedSenderType}
              onChange={(e) => setSelectedSenderType(e.target.value as 'STAFF' | 'CLIENT')}
              options={[
                { value: 'STAFF', label: 'Staff' },
                { value: 'CLIENT', label: 'Client' },
              ]}
            />
          </div>
        )}

        <div>
          <Label htmlFor="message">Message</Label>
          <ReactQuill
            theme="snow"
            value={message}
            onChange={setMessage}
            placeholder="Type your message here..."
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ color: [] }, { background: [] }],
                ['link', 'image'],
                ['clean'],
              ],
            }}
            className={colors.surface}
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="default" disabled={isLoading || !message.trim()}>
            {isLoading ? 'Sending...' : 'Send Message'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default MessageInput;
